import { Injectable } from '@angular/core';

/** The box each table cell registers; a mutation anywhere inside belongs to the nearest one. */
const OBSERVED_SELECTOR = '.table-cell__overflow-container';

/**
 * Tells each table cell when to re-check whether its text is cut off: on a resize of the cell (a
 * column dragged, the window narrowed) and on a change of its content (text arriving, the language
 * switched). One pair of observers serves every cell on the page, and the checks of a burst run
 * together in the next frame, where reading layout is safe.
 */
@Injectable({ providedIn: 'root' })
export class TableOverflowObserver {
  private readonly checks = new Map<Element, () => void>();
  private readonly pending = new Set<Element>();
  private frameRequested = false;

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;

  observe(element: HTMLElement, check: () => void): void {
    if (typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }

    this.resizeObserver ??= new ResizeObserver((entries) =>
      this.queue(entries.map((entry) => entry.target)),
    );
    this.mutationObserver ??= new MutationObserver((records) =>
      this.queue(records.map((record) => this.observedAncestor(record.target))),
    );

    this.checks.set(element, check);
    this.resizeObserver.observe(element);
    this.mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });
    this.queue([element]);
  }

  unobserve(element: HTMLElement): void {
    this.checks.delete(element);
    this.pending.delete(element);
    this.resizeObserver?.unobserve(element);
    // A MutationObserver can't drop one target; the disconnected element simply stops mattering.
  }

  private observedAncestor(node: Node): Element | null {
    const element = node instanceof Element ? node : node.parentElement;
    const observed = element?.closest(OBSERVED_SELECTOR);
    return observed && this.checks.has(observed) ? observed : null;
  }

  private queue(elements: (Element | null)[]): void {
    for (const element of elements) {
      if (element && this.checks.has(element)) {
        this.pending.add(element);
      }
    }

    if (this.pending.size === 0 || this.frameRequested) {
      return;
    }

    this.frameRequested = true;
    requestAnimationFrame(() => this.flush());
  }

  private flush(): void {
    this.frameRequested = false;
    const elements = [...this.pending];
    this.pending.clear();
    elements.forEach((element) => this.checks.get(element)?.());
  }
}
